import { FC, useMemo } from 'react';

import { useFormik } from 'formik';
import { isNullish } from 'remeda';
import useSWR from 'swr';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { CasinoAdForm } from './form';
import { getActiveAreaAdMaxAdditionalValueLength, getValidationSchema } from './validationSchema';

import { adsService } from 'v2/api/adsService';
import { casinoService } from 'v2/api/casinoService';
import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { Select } from 'v2/components/select/Select';
import { Switch } from 'v2/components/switch/Switch';
import { SelectOption } from 'v2/types';

interface CasinoAdFormDialogProps {
  title: string;
  initialValues: CasinoAdForm;
  isOpen: boolean;
  onClose: VoidFunction;
  isSubmitting: boolean;
  onSubmit: (values: CasinoAdForm, resetForm: VoidFunction) => void;
}

export const CasinoAdFormDialog: FC<CasinoAdFormDialogProps> = ({
  title,
  isOpen,
  onClose,
  initialValues,
  isSubmitting,
  onSubmit,
}) => {
  const { data: ads } = useSWR(isOpen && 'getAds', adsService.getAds);
  const { data: casinos } = useSWR(isOpen && 'getCasinos', casinoService.getCasinos);

  const validationSchema = useMemo(() => getValidationSchema(ads), [ads]);

  const formik = useFormik<CasinoAdForm>({
    initialValues,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      onSubmit(values, resetForm);
    },
    validateOnChange: false,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const adsOptions: SelectOption[] = useMemo(() => {
    if (isNullish(ads)) return [];

    return ads.map((a) => ({ id: a.id, value: a.id, label: a.key }));
  }, [ads]);

  const casinoOptions: SelectOption[] = useMemo(() => {
    if (isNullish(casinos)) return [];

    return casinos.map((c) => ({ id: c.id, value: c.id, label: c.name }));
  }, [casinos]);

  const handleSelectChange = (field: keyof CasinoAdForm) => (value: unknown) => {
    formik.setFieldValue(field, value);

    if (field === 'adId') {
      formik.validateField('additionalValue');
    }
  };

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  const selectedAdKey = useMemo(() => {
    return ads?.find((a) => a.id === formik.values.adId)?.key;
  }, [ads, formik.values.adId]);

  const maxAdditionalValueLength = getActiveAreaAdMaxAdditionalValueLength(selectedAdKey);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent className="form">
          <TextField
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Ad Name"
            required
          />

          <div className="form-group">
            <Select
              value={formik.values.adId}
              options={adsOptions}
              onChange={handleSelectChange('adId')}
              label="Ad"
              required
            />

            <Select
              value={formik.values.casinoId}
              options={casinoOptions}
              onChange={handleSelectChange('casinoId')}
              label="Casino"
              required
            />
          </div>

          <TextField
            name="additionalValue"
            value={formik.values.additionalValue ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Value"
            error={!!formik.touched.additionalValue && !!formik.errors.additionalValue}
            helperText={formik.touched.additionalValue && formik.errors.additionalValue}
            slotProps={maxAdditionalValueLength ? { htmlInput: { maxLength: maxAdditionalValueLength } } : undefined}
          />

          <div className="form-group">
            <FileSelector
              value={formik.values.image390Id ?? ''}
              label="Ad Image (390px)"
              onSelect={handleSelectChange('image390Id')}
            />

            <FileSelector
              value={formik.values.image768Id ?? ''}
              label="Ad Image (768px)"
              onSelect={handleSelectChange('image768Id')}
            />

            <FileSelector
              value={formik.values.image1024Id ?? ''}
              label="Ad Image (1024px)"
              onSelect={handleSelectChange('image1024Id')}
            />

            <FileSelector
              value={formik.values.image1300Id ?? ''}
              label="Ad Image (1300px)"
              onSelect={handleSelectChange('image1300Id')}
            />
          </div>

          <div className="form-group">
            <Switch name="isVideo" checked={formik.values.isVideo} onChange={formik.handleChange} label="Video" />

            <Switch name="isActive" checked={formik.values.isActive} onChange={formik.handleChange} label="Active" />
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button loading={isSubmitting} type="submit" variant="contained">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
